import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, HostBinding, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ButtonComponent } from '@rpgc-ai/ui/button/button.component';
import { SwipeDirective } from '@rpgc-ai/ui/swipe/swipe.directive';

import type { ModalEntry } from '@rpgc-ai/services/modal/modal.service';
import { ModalService } from '@rpgc-ai/services/modal/modal.service';

import type { IModalComponent } from '@rpgc-ai/shared-interfaces/tokens/modal.token';
import { EBackground, MODAL_COMPONENT, Padding, Scroll } from '@rpgc-ai/shared-interfaces/tokens/modal.token';

import { TranslocoPipe } from '@jsverse/transloco';

/**
 * ModalComponent — универсальный компонент модального окна.
 *
 * @link https://yt.devfast.io/youtrack/articles/NT-A-26/Modalnye-okna
 */
@Component({
  selector: 'app-ui-modal',
  styleUrl: './styles/modal.component.scss',
  templateUrl: './modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MODAL_COMPONENT, useExisting: ModalComponent }],
  imports: [SwipeDirective, NgTemplateOutlet, NgComponentOutlet, ButtonComponent, TranslocoPipe],
})
export class ModalComponent implements OnDestroy, IModalComponent {
  readonly title = model<string>();
  readonly hideCloseButton = model<boolean>(false);
  readonly keyboardOverlay = model<boolean>(false);
  readonly entry = input<ModalEntry | null>(null);
  readonly #dialogEntry = inject<ModalEntry | null>(DIALOG_DATA, { optional: true });

  readonly dismiss = output<void>();

  @HostBinding('class')
  get applyStylesToHost(): string {
    return this.#hostClasses();
  }

  readonly isClosing = signal(false);
  readonly paddingStyle = signal<Padding>(Padding.SHOW);
  readonly backgroundStyle = signal<EBackground>(EBackground.SHOW);
  readonly scrollStyle = signal<Scroll>(Scroll.SHOW);
  readonly hostClass = signal<string>('');
  readonly disableCloseOnSwipe = signal(false);
  readonly closeRequestHandler = signal<(() => void) | null>(null);

  readonly #modalService = inject(ModalService);
  readonly #dialogRef = inject(DialogRef, { optional: true });
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);
  readonly #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly modalEntry = computed<ModalEntry | null>(() => this.entry() ?? this.#dialogEntry ?? null);

  readonly label = computed<string | undefined>(() => this.title() ?? this.modalEntry()?.options?.title);
  // Когда true — снимаем дефолтный tma:pb-safe-0 на .modal_body
  // (для модалок которые сами управляют bottom inset).
  readonly disableTmaBodySafeArea = computed<boolean>(() => this.modalEntry()?.options?.disableTmaBodySafeArea ?? false);
  readonly isFullScreen = computed<boolean>(() => this.modalEntry()?.options?.fullScreen ?? false);
  readonly #hostClasses = computed<string>(() => [
    this.hostClass(),
    this.isFullScreen() ? 'modal-host_full_screen' : '',
    this.keyboardOverlay() ? 'modal-host_keyboard-overlay' : '',
  ].filter(Boolean).join(' '));

  // Когда true — снимаем дефолтный mobile:pb-safe-0 на .modal_body
  // (для модалок которые сами управляют bottom inset в Capacitor-сборке).
  readonly disableMobileBodySafeArea = computed<boolean>(() => this.modalEntry()?.options?.disableMobileBodySafeArea ?? false);

  readonly #animationDuration = 300;
  readonly Padding = Padding;
  readonly EBackground = EBackground;
  readonly Scroll = Scroll;

  #closeTimer: ReturnType<typeof setTimeout> | null = null;
  #keyboardScrollTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.#keepFocusedFieldVisible();

    this.#dialogRef?.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (!this.modalEntry()?.options?.disableOverlayClose) {
          this.requestClose();
        }
      });

    this.#modalService.programmaticClose$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((req) => {
      if (req.entryId !== this.modalEntry()?.id) {
        return;
      }
      if (this.isClosing()) {
        req.done();
        return;
      }
      this.#closeWithOptionalResult(req.result, req.done);
    });

    this.#destroyRef.onDestroy(() => {
      // Catch-all keyboard dismiss for direct-close paths (clearStack / priority restoration)
      // that destroy the shell without running the close timer. Idempotent with the timer blur.
      if (this.#document.body.classList.contains('mobile-mode')) {
        const active = this.#document.activeElement;
        if (active instanceof HTMLElement) {
          active.blur();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.#keyboardScrollTimer !== null) {
      clearTimeout(this.#keyboardScrollTimer);
      this.#keyboardScrollTimer = null;
    }
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  handleSwipeDown(): void {
    if (this.disableCloseOnSwipe()) {
      return;
    }
    if (this.closeRequestHandler()) {
      this.requestClose();
      return;
    }
    if (this.modalEntry()?.options?.closeOnlyTopOnSwipe) {
      this.close();
      return;
    }
    this.#closeWithOptionalResult(undefined, () => {
      if (this.#modalService.hasCurrentModal()) {
        this.#modalService.clearStack();
      }
    });
  }

  handleDragHitboxClick(): void {
    if (this.modalEntry()?.options?.disableOverlayClose) {
      return;
    }

    this.requestClose();
  }

  requestClose(): void {
    const handler = this.closeRequestHandler();
    if (handler) {
      handler();
      return;
    }

    this.close();
  }

  close(): void {
    this.#closeWithOptionalResult(undefined);
  }

  closeDialogWithResult(result?: unknown): void {
    this.#closeWithOptionalResult(result);
  }

  #keepFocusedFieldVisible(): void {
    afterNextRender(() => {
      const viewport = this.#document.defaultView?.visualViewport;
      if (!viewport) { return; }
      // Programmatic OTP focus uses preventScroll; reveal it after keyboard geometry settles.
      const onViewportChange = (): void => {
        if (this.#keyboardScrollTimer !== null) { clearTimeout(this.#keyboardScrollTimer); }
        this.#keyboardScrollTimer = setTimeout(() => {
          this.#keyboardScrollTimer = null;
          const active = this.#document.activeElement;
          if (active instanceof HTMLElement && this.#elementRef.nativeElement.contains(active) &&
            (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active.isContentEditable)) {
            active.scrollIntoView({ block: 'nearest' });
          }
        }, 250);
      };
      viewport.addEventListener('resize', onViewportChange);
      viewport.addEventListener('scroll', onViewportChange);
      this.#destroyRef.onDestroy(() => {
        viewport.removeEventListener('resize', onViewportChange);
        viewport.removeEventListener('scroll', onViewportChange);
      });
    });
  }

  #closeWithOptionalResult(result: unknown, onShellExitComplete?: () => void): void {
    this.isClosing.set(true);

    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    this.#closeTimer = setTimeout(() => {
      // Dismiss the soft keyboard at the END of the close animation so the lifted
      // sheet doesn't drop to bottom:0 mid slide-down (the blur republishes inset→0).
      // Capacitor only: dismiss the soft keyboard with the modal. No-op on web/TMA/desktop.
      if (this.#document.body.classList.contains('mobile-mode')) {
        const active = this.#document.activeElement;
        if (active instanceof HTMLElement) {
          active.blur();
        }
      }
      if (this.#dialogRef) {
        this.#dialogRef.close(result);
      }
      this.isClosing.set(false);
      this.#closeTimer = null;
      onShellExitComplete?.();
    }, this.#animationDuration);

    this.dismiss.emit();
  }
}
